// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { useCallback, useEffect, useRef, useState } from "react";

import { mdiAlert } from "@mdi/js";
import { Select } from "antd";
import _debounce from "lodash/debounce";
import styled from "styled-components";

import { FORECAST_SOURCE_SETTING_TYPE } from "../../../../store/features";
import useForecastPreferenceQuery from "../../../arps/queries/useForecastPreferenceQuery";
import { useUserContext } from "../../../user/context";
import { updateForecastSource } from "../../../user/context/reducer/UserReducer";
import { useUserDefaults, useUserSettingMutation } from "../../../user/hooks";
import {
  DEFAULT_FORECAST_SOURCE_SETTING,
  FORECAST_SOURCES,
  SYSTEM_DEFAULT_FORECAST_SOURCE_OPTION
} from "../../constants/userSetting.constants";
import { DefaultForecastSource } from "../../models";
import {
  ErrorContainer,
  SectionContainer,
  SectionHeading,
  SectionHeadingTitleContainer,
  SectionInputWrapper
} from "./shared";

const DefaultForecastSourceSetting = () => {
  const [, dispatch] = useUserContext();
  const defaults = useUserDefaults();
  const defaultForecastSetting = defaults?.forecastSourceSetting;
  const mutation = useUserSettingMutation<DefaultForecastSource>(
    DEFAULT_FORECAST_SOURCE_SETTING
  );
  const forecastPreference = useForecastPreferenceQuery().data;

  const [updateError, setUpdateError] = useState("");
  // The folder id alone, not the whole setting: the id is read from context at write time
  // (handleForecastSettingChange), so keeping a copy of it here bought nothing and could go
  // stale behind the resync effect below, whose dependency is this same value.
  const [currentForecastFolderId, setCurrentForecastFolderId] = useState<
    Maybe<FORECAST_SOURCE_SETTING_TYPE>
  >(defaultForecastSetting?.forecastFolderId);
  // True between a user's pick and its debounced write settling, so the resync effect below
  // knows the local value is newer than the stored one.
  const hasPendingEditRef = useRef(false);

  // determine options for forecast source based on data source access
  const forecastSourceOptions = [];
  if (forecastPreference && Array.isArray(forecastPreference)) {
    const sortedData = forecastPreference.sort(
      (a: { order: number }, b: { order: number }) => a.order - b.order
    );

    for (const source of sortedData) {
      const dataSourceDetails = FORECAST_SOURCES[source.dataSourceName];
      if (dataSourceDetails) {
        forecastSourceOptions.push({
          value: dataSourceDetails.folderId,
          label: dataSourceDetails.name
        });
      }
    }
  }

  // Offered to every org, unconditionally. "System Default" is a policy, not a data source:
  // it means "every forecast source this org can use", and useEffectiveForecastFolderId
  // narrows it at read time, so an org without Novi credentials that picks it gets
  // McDaniel-only with excludeNovi set. Nothing inaccessible is ever selectable (EVA-180).
  //
  // It was previously gated on useHasNoviAccess, which reads the bare boolean with no
  // isAuthoritative check — so a soft-failed data-source lookup removed the option for the
  // whole session, and a non-Novi org could never choose it at all. Unconditional also means
  // the option list no longer changes shape when the availability answer lands.
  forecastSourceOptions.unshift(SYSTEM_DEFAULT_FORECAST_SOURCE_OPTION);

  const debouncedSettingsChange = _debounce((settings: DefaultForecastSource) => {
    mutation.mutate(settings);
  }, 400);

  const handleForecastSettingChange = useCallback(
    (setting: FORECAST_SOURCE_SETTING_TYPE) => {
      const newSetting = {
        id: defaultForecastSetting?.id,
        forecastFolderId: setting
      };
      hasPendingEditRef.current = true;
      debouncedSettingsChange(newSetting);
      setCurrentForecastFolderId(setting);
    },
    [defaultForecastSetting?.id]
  );

  // Seeded once at mount and then holding the optimistic value during the 400ms debounce, so
  // it must be resynced whenever the stored value resolves or changes underneath it — the
  // panel can mount before the settings query returns, and the loader's System Default switch
  // writes mid-session. Without this the Select keeps rendering the mount-time seed while the
  // stored value has already moved on, which is the mismatch EVA-180 was reported as.
  const storedForecastFolderId = defaultForecastSetting?.forecastFolderId;
  useEffect(() => {
    // Yield to an uncommitted local pick. The write is debounced 400ms, and the stored value
    // can change inside that window — the loader's System Default write landing, or any other
    // updateForecastSource dispatch. Resyncing then would replace what the user just chose,
    // and the mutation effect below would put it back once the debounced write returned: on
    // screen the Select reverts and then restores itself.
    if (!storedForecastFolderId || hasPendingEditRef.current) return;
    setCurrentForecastFolderId(storedForecastFolderId);
  }, [storedForecastFolderId]);

  useEffect(() => {
    if (!mutation?.data?.id) return;
    // The pick is committed; the stored value is authoritative again.
    hasPendingEditRef.current = false;
    updateForecastSource(dispatch, {
      id: mutation.data.id,
      forecastFolderId: mutation.data.forecastFolderId
    });
  }, [mutation?.data]);

  useEffect(() => {
    if (mutation?.error) {
      // Cleared on failure too, or one failed write would pin the flag and disable resync for
      // the rest of the session.
      hasPendingEditRef.current = false;
      // eslint-disable-next-line no-console
      console.error(mutation?.error);
      setUpdateError("Failed to update default forecast source.");
    } else {
      setUpdateError("");
    }
  }, [mutation?.error]);

  return (
    <SectionContainer>
      <SectionHeading>
        <SectionHeadingTitleContainer>
          Forecast Source
          {updateError && (
            <ErrorContainer>
              <Icon path={mdiAlert} size={1} /> {updateError}
            </ErrorContainer>
          )}
        </SectionHeadingTitleContainer>
      </SectionHeading>
      <SectionInputWrapper>
        <StyledSelect
          data-testid="user-default-forecast-source-select"
          value={currentForecastFolderId}
          onChange={handleForecastSettingChange}
          options={forecastSourceOptions}
        />
      </SectionInputWrapper>
    </SectionContainer>
  );
};

export default DefaultForecastSourceSetting;

export const StyledSelect = styled(Select)`
  height: 50px;
  display: flex;
  align-items: center;

  .ant-select-selector {
    display: flex;
    align-items: center;
    height: 100% !important;
  }
`;
